import { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/button/Button';
import Card from '../../../components/card/Card';
import Icon from '../../../components/icon/Icon';
import Spinner from '../../../components/Spinner/spinner';
import P from '../../../components/texts/p/P';
import { legalEntities } from '../../../mocks/LegalEntities';
import {
  downloadClientDocument,
  downloadLufeCloudFile,
} from '../../../services/ClientService';
import { downloadApplicationFile } from '../../../services/ApplicationService';
import { documentData } from '../../../types/interfaces';
import { getMimeType } from '../../../utils/Utils';
import { isLufeCloudFileName } from '../../../utils/isLufeCloudFileName';
import { getOtherLegalEntitiesFileTypes } from '../../../services/ApplicationService';
import { useConfig } from '../../../context/ConfigContext';
import {
  getRequiredDocumentsForLegalEntity,
  findDocumentForRequiredSlot,
  isOriginationExtraDocument,
  shouldShowOptionalDocumentInSummary,
} from '../../../utils/originationRequiredDocuments';
import {
  buildDocumentDedupKey,
  deduplicateDocumentsForDisplay,
} from '../../../utils/documentKey';

const EXCLUDED_INSTRUMENTATION_TYPES = ['013', '014', '015', '016', '017', '018'];

interface DocumentProps {
  documents: documentData[];
  legalEntity: string;
  cuit: string;
  applicationId?: string;
}

const SummaryDocuments: React.FC<DocumentProps> = ({
  documents,
  legalEntity,
  cuit,
  applicationId,
}) => {
  const config = useConfig();
  const [isSaving, setIsSaving] = useState<boolean[]>([]);
  const [otherLegalEntities, setOtherLegalEntities] = useState<Array<{id: number; code: string; description: string}>>([]);

  // Fetch de tipos de documentos al montar el componente
  useEffect(() => {
    const fetchOtherLegalEntities = async () => {
      try {
        const response = await getOtherLegalEntitiesFileTypes();
        if (response.success && response.data) {
          setOtherLegalEntities(response.data);
        }
      } catch (error) {
        console.error('Error fetching other legal entities:', error);
      }
    };

    fetchOtherLegalEntities();
  }, []);

  const handleDownloadFile = (filename: string, index: number) => {
    setIsSaving((prev) => {
      const newSavingState = [...prev];
      newSavingState[index] = true;
      return newSavingState;
    });

    if (isLufeCloudFileName(filename)) {
      downloadLufeCloudFile(cuit, filename).then((response) => {
        if (response.success) {
          const fileBlob = new Blob([response.data], {
            type: 'application/pdf',
          });
          const url = window.URL.createObjectURL(fileBlob);
          window.open(url, '_blank');
        }
        setIsSaving((prev) => {
          const newSavingState = [...prev];
          newSavingState[index] = false;
          return newSavingState;
        });
      });
    } else {
      // Si hay applicationId, usar el endpoint de aplicaciones
      const downloadPromise = applicationId
        ? downloadApplicationFile(cuit, applicationId, filename)
        : downloadClientDocument(cuit, filename);
      
      downloadPromise.then((response) => {
        if (response.success) {
          const mimeType = getMimeType(filename);
          const fileBlob = new Blob([response.data], { type: mimeType });
          const url = window.URL.createObjectURL(fileBlob);

          if (
            mimeType.startsWith('application/pdf') ||
            mimeType.startsWith('image/')
          ) {
            window.open(url, '_blank');
          } else {
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
          }
        }
        setIsSaving((prev) => {
          const newSavingState = [...prev];
          newSavingState[index] = false;
          return newSavingState;
        });
      });
    }
  };

  const requiredDocuments = getRequiredDocumentsForLegalEntity(
    legalEntity,
    legalEntities,
    config
  ).filter((document) => shouldShowOptionalDocumentInSummary(document, documents));

  const extraDocumentsForDisplay = useMemo(() => {
    const extras = documents.filter(
      (doc) =>
        !EXCLUDED_INSTRUMENTATION_TYPES.includes(doc.type) &&
        isOriginationExtraDocument(doc, requiredDocuments)
    );
    return deduplicateDocumentsForDisplay(extras);
  }, [documents, requiredDocuments]);

  return (
    <Card margin={{ top: '5' }}>
      <P type='1' weight='bold' variant='neutral'>
          Documentación
        </P>
      <Card
        display='flex'
        flexWrap='wrap'
        gap='20px'
        justifyContent='space-between'
        margin={{ top: '3' }}
      >
        {requiredDocuments.map((document, index) => {
          const matchedDocument = findDocumentForRequiredSlot(documents, document.id);
          return matchedDocument ? (
              <Card
                key={index}
                width='48%'
                display='flex'
                height='auto'
                alignItems='center'
                gap='16px'
              >
                <Icon name='done_outline' variant='success' />
                <Card width='-webkit-fill-available'>
                  <P withMargin={false} type='2' variant='neutral' align='left'>
                    {' '}
                    {document.value}
                  </P>
                </Card>
                <Card display='flex' justifyContent='center' width='75px'>
                  {isSaving[index] ? (
                    <Spinner size='md' variant='primary' />
                  ) : (
                    <Button
                      variant='secondary'
                      size='md'
                      onClick={() => {
                        const filename = matchedDocument.file_name;
                        if (filename) {
                          handleDownloadFile(filename, index);
                        }
                      }}
                    >
                      <Icon
                        name='view_outline'
                        height={44}
                        variant='neutral'
                        cursor='pointer'
                      />
                    </Button>
                  )}
                </Card>
              </Card>
            ) : (
              <Card
                key={index}
                width='48%'
                display='flex'
                height='40px'
                alignItems='center'
                gap='16px'
              >
                <Icon name='cancel_outline' variant='danger' />
                <Card width='-webkit-fill-available'>
                  <P
                    withMargin={false}
                    type='2'
                    variant='neutral'
                    align='left'
                    modifier='weak'
                  >
                    {' '}
                    {document.value}
                  </P>
                </Card>
              </Card>
            );
        })}
      </Card>
      {extraDocumentsForDisplay.length > 0 && (
        <Card margin={{ top: '5' }}>
          <P type='1' weight='bold' variant='neutral'>
            Documentación extra
          </P>
          <Card
            display='flex'
            flexWrap='wrap'
            gap='20px'
            justifyContent='space-between'
          >
            {extraDocumentsForDisplay.map((document, index) => (
                  <Card
                    key={buildDocumentDedupKey(document.type, document.file_name)}
                    width='48%'
                    display='flex'
                    alignItems='center'
                    gap='16px'
                    margin={{ bottom: '0' }}
                  >
                    <Icon name='done_outline' variant='success' />
                    <Card width='-webkit-fill-available'>
                      <P
                        withMargin={false}
                        type='2'
                        variant='neutral'
                        align='left'
                      >
                        {
                          otherLegalEntities.find(
                            (item) => item.code === document.type
                          )?.description
                        }
                      </P>
                    </Card>
                    <Card display='flex' justifyContent='center' width='75px'>
                      {isSaving[index] ? (
                        <Spinner size='md' variant='primary' />
                      ) : (
                        <Button
                          variant='secondary'
                          size='md'
                          onClick={() =>
                            handleDownloadFile(document.file_name, index)
                          }
                        >
                          <Icon
                            name='view_outline'
                            height={44}
                            variant='neutral'
                            cursor='pointer'
                          />
                        </Button>
                      )}
                    </Card>
                  </Card>
            ))}
          </Card>
        </Card>
      )}
    </Card>
  );
};

export default SummaryDocuments;
